import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthApiError, useAuth } from "@/lib/auth";
import type { AdminUserRow } from "@/lib/auth/admin-api";
import { deleteUser } from "@/lib/auth/admin-api";

interface DeleteUserDialogProps {
  open: boolean;
  user: AdminUserRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// Hard confirmation dialog for soft-deleting a user (the backend disables
// the row + revokes sessions but keeps the record so the email stays
// UNIQUE). The admin must retype the target email before the Delete button
// activates — copy/paste counts; case is ignored to stay friendly.
export function DeleteUserDialog({
  open,
  user,
  onOpenChange,
  onSuccess,
}: DeleteUserDialogProps) {
  const { user: principal } = useAuth();
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!user) return null;
  const isSelf = principal?.id === user.id;
  const typedMatches = typed.trim().toLowerCase() === user.email.toLowerCase();

  const handleConfirm = async () => {
    if (submitting || !typedMatches || isSelf) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteUser(user.id);
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === "self_modification_blocked") {
          setError("You cannot delete yourself.");
        } else if (err.code === "last_admin") {
          setError(
            "At least one active admin must remain. Promote another user to admin first.",
          );
        } else if (err.code === "not_found") {
          setError("That user no longer exists. Refresh the list and try again.");
        } else {
          setError(err.serverMessage ?? "Couldn't delete user. Please try again.");
        }
      } else {
        setError("Couldn't reach the server. Check your connection and retry.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !submitting && onOpenChange(next)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete user</DialogTitle>
          <DialogDescription>
            This disables the account, revokes all sessions and trusted
            devices, and keeps the email reserved so it can&apos;t be
            re-registered.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-sm text-foreground">
            To confirm, retype{" "}
            <span className="font-mono text-foreground">{user.email}</span>{" "}
            below.
          </p>
          <Label htmlFor="delete-confirm-email" className="sr-only">
            Retype email to confirm
          </Label>
          <Input
            id="delete-confirm-email"
            type="email"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={user.email}
            disabled={submitting}
            autoFocus
          />
        </div>

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting || !typedMatches || isSelf}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              "Delete user"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
